<script setup lang="ts">
import { Loader2, UserMinus, UserPlus } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { clearNotifications, showNotification } from '@/lib/utils';
import {
    addMemberToGroup,
    getGroupMembers,
    type GroupMember,
    removeMemberFromGroup,
    type UserGroup
} from '@/services/user_groups';

import Label from '../ui/label/Label.vue';

interface Props {
    open: boolean;
    group: UserGroup | null;
    tenantId: string;
}

interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'member-added', data: { groupId: number; memberCount: number }): void;
    (e: 'member-removed', data: { groupId: number; memberCount: number }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Reactive state
const members = ref<GroupMember[]>([]);
const newMemberEmail = ref('');
const loading = ref(false);
const adding = ref(false);
const removing = ref<string | null>(null);
const error = ref<string | null>(null);
const validationMessage = ref<string | null>(null);

// Computed
const isOpen = computed({
    get: () => props.open,
    set: (value) => emit('update:open', value)
});

// Watch for dialog open/close
watch(
    () => props.open,
    (newValue) => {
        if (newValue && props.group) {
            loadMembers();
            resetForm();
        }
    }
);

// Methods
const resetForm = () => {
    newMemberEmail.value = '';
    clearNotifications(validationMessage, error);
};

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

const loadMembers = async () => {
    if (!props.group) return;

    loading.value = true;
    clearNotifications(validationMessage, error);

    try {
        const response = await getGroupMembers(props.group.id, props.tenantId);
        // Sort members alphabetically by email address
        members.value = response.members.sort((a, b) => {
            const emailA = a.email || '';
            const emailB = b.email || '';
            return emailA.localeCompare(emailB);
        });
    } catch (err) {
        console.error('Error loading members:', err);
        showNotification(
            validationMessage,
            error,
            'error',
            'Failed to load group members'
        );
    } finally {
        loading.value = false;
    }
};

const validateAndAddMember = async () => {
    if (!props.group || !newMemberEmail.value.trim()) return;

    const emailToAdd = newMemberEmail.value.trim();

    // Validate email format first
    if (!isValidEmail(emailToAdd)) {
        showNotification(
            validationMessage,
            error,
            'error',
            'Please enter a valid email address'
        );
        return;
    }

    // Check if user is already in the group
    const isAlreadyMember = members.value.some(
        (member) => member.email?.toLowerCase() === emailToAdd.toLowerCase()
    );
    if (isAlreadyMember) {
        showNotification(
            validationMessage,
            error,
            'error',
            'User is already a member of this group'
        );
        return;
    }

    adding.value = true;
    clearNotifications(validationMessage, error);

    try {
        // Add the user to the group (this will validate user existence internally)
        const newMember = await addMemberToGroup(
            props.group.id,
            props.tenantId,
            emailToAdd
        );

        // Add the new member to the local array
        members.value.push(newMember);
        // Sort members alphabetically by email address
        members.value.sort((a, b) => {
            const emailA = a.email || '';
            const emailB = b.email || '';
            return emailA.localeCompare(emailB);
        });

        resetForm();
        emit('member-added', {
            groupId: props.group.id,
            memberCount: members.value.length
        });
        showNotification(
            validationMessage,
            error,
            'success',
            `Successfully added ${newMember.name || newMember.email} to the group`
        );
    } catch (err: any) {
        console.error('Error adding member:', err);

        // Handle different error cases based on error message
        const errorMessage = err.message || '';
        const message = errorMessage.includes('not found')
            ? `"${emailToAdd}" is not registered in the system. Users must sign up or be invited to the platform before they can be added to groups.`
            : 'Failed to add user to group';

        showNotification(validationMessage, error, 'error', message);
    } finally {
        adding.value = false;
    }
};

const removeMember = async (member: GroupMember) => {
    if (!props.group) return;

    removing.value = member.user_id;
    clearNotifications(validationMessage, error);

    try {
        await removeMemberFromGroup(
            props.group.id,
            member.user_id,
            props.tenantId
        );
        // Remove the member from the local array
        const memberIndex = members.value.findIndex(
            (m) => m.user_id === member.user_id
        );
        if (memberIndex !== -1) {
            members.value.splice(memberIndex, 1);
        }
        emit('member-removed', {
            groupId: props.group.id,
            memberCount: members.value.length
        });
    } catch (err) {
        console.error('Error removing member:', err);
        showNotification(
            validationMessage,
            error,
            'error',
            'Failed to remove user from group'
        );
    } finally {
        removing.value = null;
    }
};

const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
        validateAndAddMember();
    }
};
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogContent class="max-w-2xl h-[90vh] flex flex-col">
            <DialogHeader>
                <DialogTitle>Manage Group Members</DialogTitle>
            </DialogHeader>

            <div class="flex-shrink-0 space-y-4 pb-4 pt-2">
                <div>
                    <Label>Add New Member</Label>
                    <div class="flex gap-3 justify-between">
                        <Input
                            v-model="newMemberEmail"
                            type="email"
                            placeholder="Enter user email"
                            :disabled="adding"
                            @keypress="handleKeyPress"
                            class="flex-1"
                        />
                        <Button
                            @click="validateAndAddMember"
                            :disabled="!newMemberEmail.trim() || adding"
                        >
                            <Loader2
                                v-if="adding"
                                class="w-4 h-4 mr-2 animate-spin"
                            />
                            <UserPlus v-else class="w-4 h-4 mr-2" />
                            Add Member
                        </Button>
                    </div>

                    <!-- Validation/Success Message -->
                    <div
                        v-if="validationMessage"
                        class="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm"
                    >
                        {{ validationMessage }}
                    </div>

                    <!-- Error Message -->
                    <div
                        v-if="error"
                        class="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm"
                    >
                        {{ error }}
                    </div>
                </div>
            </div>

            <!-- Members List Section -->
            <div class="flex-1 flex flex-col min-h-0">
                <p class="font-semibold mb-3 flex-shrink-0">
                    Current Members<span v-if="!loading">
                        ({{ members.length }})
                    </span>
                </p>
                <div class="flex-1 min-h-0 overflow-y-auto">
                    <div
                        v-if="loading"
                        class="flex items-center justify-center h-32"
                    >
                        <Loader2 class="w-6 h-6 animate-spin" />
                        <span class="ml-2">Loading members...</span>
                    </div>

                    <div
                        v-else-if="members.length === 0"
                        class="flex items-center justify-center h-32 text-muted-foreground"
                    >
                        No members in this group yet
                    </div>

                    <div v-else class="space-y-2">
                        <div
                            v-for="member in members"
                            :key="member.user_id"
                            class="flex items-center justify-between p-3 bg-card border rounded-lg"
                        >
                            <div class="flex-1 flex items-center gap-4">
                                <div class="flex-1">
                                    <p v-if="member.name" class="font-medium">
                                        {{ member.name }}
                                    </p>
                                    <p v-else class="text-muted-foreground">
                                        Name not available
                                    </p>
                                </div>
                                <div class="flex-1">
                                    <p class="text-sm text-muted-foreground">
                                        {{ member.email }}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                @click="removeMember(member)"
                                :disabled="removing === member.user_id"
                                class="text-destructive hover:text-destructive"
                            >
                                <Loader2
                                    v-if="removing === member.user_id"
                                    class="w-4 h-4 animate-spin"
                                />
                                <UserMinus v-else class="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>
